/*
  Chrome Vector — chrome page transitions.

  This runs the SAME melt shader as the entrance, but on raw WebGL rather than
  three.js. The earlier version booted a second three.js renderer on every
  navigation, which is what made it slow; the shader itself was never the cost.
  A bare context plus one full-screen triangle compiles in a couple of
  milliseconds and downloads nothing.

  This module is the single source of truth for the chrome look: cv-logo-3d.js
  reads MELT_FRAG from here so the entrance and the page transitions can never
  drift apart.

  Flow:
    internal link click -> chrome floods down -> navigate
    next page loads     -> starts covered (class set in <head>) -> chrome drains
*/
window.CVChrome = (function () {
  'use strict';

  var INBOUND_KEY = 'cv-chrome-inbound';

  /* ---------- connection tier ----------
     effectiveType is the browser's own rolling estimate of round-trip time and
     throughput, so it reflects a weak wifi bar as well as a weak cell signal. */
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  function netTier() {
    if (conn.saveData === true) return 'slow';
    var et = conn.effectiveType || '';
    if (et === 'slow-2g' || et === '2g') return 'slow';
    if (et === '3g') return 'medium';
    return 'fast';
  }

  /* ---------- device tier ----------
     Safari exposes neither hardwareConcurrency nor deviceMemory, so on every
     iPhone, iPad and Mac there is no capability signal to read at all. What can
     always be measured is how many pixels the shader is being asked to fill,
     and that is the half of the cost that actually varies: a phone is around
     330k CSS pixels, a laptop 1.3M, an iPad in landscape 1.4M. A flat dpr cap
     across those is three completely different workloads, which is why "not a
     phone" was never a safe tier. Budget the fragments instead. */
  var COARSE = (function () {
    try { return !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches); }
    catch (e) { return false; }
  })();

  /* Touch tops out lower than a trackpad machine, but only because the GPUs
     differ — not because the screen is small. Both ceilings stay where they
     already shipped for small viewports; the budget below is what pulls them
     down on large ones. */
  function hardDpr() { return COARSE ? 1.0 : 1.25; }
  /* Floor for the opening guess. Below this the upscale is visible, and since
     there is now a measurement loop, guessing soft is the worse error: quality
     never tried is quality that cannot be recovered, whereas a device that
     cannot cope will say so within the first few frames and be remembered. */
  var BUDGET_FLOOR = 0.85;
  var MIN_DPR = 0.6;     /* only the measured path may go this low */
  var DPR_KEY = 'cv-chrome-dpr';

  function fragmentBudget() { return COARSE ? 750000 : 2200000; }

  function viewportArea() {
    return Math.max(1, (window.innerWidth || 1) * (window.innerHeight || 1));
  }

  /* Resolution that lands this viewport on the budget. Orientation changes the
     area very little but changes nothing else, so this is also the right hook
     for landscape. */
  function budgetDpr() {
    var d = Math.sqrt(fragmentBudget() / viewportArea());
    return Math.max(BUDGET_FLOOR, Math.min(hardDpr(), window.devicePixelRatio || 1, d));
  }

  /* What this device actually coped with last time. A budget is a guess; a
     measurement is not, and carrying it forward means the first transition of a
     session is the only one that has to find the level. */
  function learnedDpr() {
    try {
      var v = parseFloat(sessionStorage.getItem(DPR_KEY));
      return (v > 0 && v <= hardDpr()) ? v : 0;
    } catch (e) { return 0; }
  }
  function rememberDpr(v) {
    try { sessionStorage.setItem(DPR_KEY, String(Math.round(v * 100) / 100)); } catch (e) {}
  }

  function startDpr() {
    var learned = learnedDpr();
    return learned ? Math.min(learned, budgetDpr()) : budgetDpr();
  }

  /* Octaves scale with the same signal. A device already rendering below 1:1 is
     not going to enjoy the fourth octave either. */
  var FBM_OCT = (COARSE || startDpr() < 0.95) ? 3 : 4;
  var LOW_POWER = COARSE || startDpr() < 0.95;

  /* A fixed 520ms flood is roughly ten frames on a device managing 20fps, and
     ten frames of a fast-moving edge is what reads as stutter. Stretching the
     same motion over more frames does not make it cheaper, but it does make it
     smooth. Recomputed on orientation change because a device can move between
     tiers when the viewport does. */
  var FLOOD_MS, DRAIN_MS;
  function setDurations() {
    var slow = COARSE || startDpr() < 0.95;
    FLOOD_MS = slow ? 820 : 560;
    DRAIN_MS = slow ? 880 : 600;
  }
  setDurations();

  /* How long the chrome is allowed to sit fully covering while the next page
     finishes arriving, before it gives up waiting and reveals whatever is
     there. Slow connections get more rope. */
  function holdCap() {
    var tier = netTier();
    if (tier === 'slow') return 5000;
    if (tier === 'medium') return 3500;
    return 2200;
  }
  /* Never flash the cover for a single frame on an instant load. */
  var HOLD_MIN_MS = 220;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var navigating = false;
  var api = {};

  /* ---------- shared GLSL ---------- */

  var GLSL_CHROME_SRC = [
    'float hash11(float p){p=fract(p*0.1031);p*=p+33.33;p*=p+p;return fract(p);}',
    'float hash21(vec2 p){vec3 p3=fract(vec3(p.xyx)*0.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}',
    'float vnoise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);',
    '  float a=hash21(i),b=hash21(i+vec2(1.0,0.0)),c=hash21(i+vec2(0.0,1.0)),d=hash21(i+vec2(1.0,1.0));',
    '  return mix(mix(a,b,f.x),mix(c,d,f.x),f.y);}',
    'float fbm(vec2 p){float v=0.0,a=0.5;for(int i=0;i<OCT;i++){v+=a*vnoise(p);p*=2.03;a*=0.5;}return v;}',
    /* Domain-warped bands read as poured liquid chrome. */
    'vec3 chromeColour(vec2 p,float t){',
    '  vec2 q=vec2(fbm(p+vec2(0.0,t*0.16)),fbm(p+vec2(5.2,1.3)-t*0.11));',
    '  vec2 r=vec2(fbm(p+3.0*q+vec2(1.7,9.2)+t*0.07),fbm(p+3.0*q+vec2(8.3,2.8)));',
    '  float f=fbm(p+3.2*r);',
    '  float v=0.5+0.5*sin(f*12.0+r.x*5.0+t*1.1);',
    '  v=pow(v,1.5);',
    '  vec3 col=mix(vec3(0.035,0.055,0.085),vec3(0.42,0.50,0.58),smoothstep(0.0,0.5,v));',
    '  col=mix(col,vec3(0.98,0.99,1.0),smoothstep(0.52,0.97,v));',
    '  col=mix(col,vec3(0.50,0.79,0.94),0.13*smoothstep(0.55,1.0,v));',
    '  col=mix(col,vec3(1.0,0.31,0.85),0.05*smoothstep(0.80,1.0,v));',
    '  return col;}'
  ].join('\n');

  /* One source, two octave counts. Keeping the warp structure identical is the
     point: octaves 1..n are the same noise in both, so the large folds line up
     exactly and only the fine grain differs. That is what lets the hold hand
     over to the drain without the pattern jumping. */
  function chromeGLSL(oct) { return GLSL_CHROME_SRC.replace(/OCT/g, String(oct)); }
  var GLSL_CHROME = chromeGLSL(FBM_OCT);

  /* Leading edge of the pour, per-column tendrils, and the retreating edge.
     Cheap: one fbm and some smoothsteps. This half always runs. */
  var GLSL_EDGES = [
    '  float colId=floor(uv.x*70.0);',
    '  float rnd=hash11(colId);',
    '  float rnd2=hash11(colId+37.0);',
    '  float wob=(fbm(vec2(uv.x*4.5,uTime*0.3))-0.5)*0.09;',
    '  float tendril=rnd*0.20+rnd2*0.12;',
    '  float floodEdge=1.06-uFlood*(1.42+tendril)+wob*(1.0-uFlood*0.5);',
    '  float drainEdge=1.18-uDrain*(1.40+rnd*0.55)+wob*0.6;',
    '  float soft=0.008;',
    '  float sheet=smoothstep(floodEdge-soft,floodEdge+soft,uv.y);',
    '  if(uDrain>0.0){sheet*=1.0-smoothstep(drainEdge-soft,drainEdge+soft,uv.y);}',
    '  float lipA=(1.0-smoothstep(0.0,0.055,abs(uv.y-floodEdge)))*(1.0-uDrain);',
    '  float lipB=uDrain>0.0?(1.0-smoothstep(0.0,0.05,abs(uv.y-drainEdge))):0.0;'
  ].join('\n');

  /* Expensive: five domain-warped fbm calls. Both consumers multiply the result
     by `sheet`, so any pixel the chrome has not reached yet was paying full
     price for a colour that got multiplied straight back to zero. Guarding it
     means only the poured band actually evaluates the noise. */
  var GLSL_CHROME_EVAL = [
    '  vec3 chrome=vec3(0.0);',
    '  if(sheet>0.0008){',
    '    vec2 cp=vec2(uv.x*uAspect,uv.y)*3.0;',
    '    chrome=chromeColour(cp+vec2(0.0,-uFlood*0.5+uDrain*0.9),uTime);',
    '    chrome+=vec3(0.85,0.93,1.0)*(lipA*0.55+lipB*0.7);',
    '  }'
  ].join('\n');

  var GLSL_SHEET = GLSL_EDGES + '\n' + GLSL_CHROME_EVAL;

  var UNIFORMS = [
    'uniform float uTime;',
    'uniform float uFlood;',
    'uniform float uDrain;',
    'uniform float uAspect;'
  ].join('\n');

  /* Full melt, used by the entrance: composites the 3D badge behind the chrome. */
  api.MELT_FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform sampler2D tScene;',
    UNIFORMS,
    GLSL_CHROME,
    'void main(){',
    '  vec2 uv=vUv;',
    /* Standing lobby: nothing is melting, so this is a straight blit of the
       badge. Without this early-out the idle screen was still paying for the
       full domain-warped chrome on every pixel of every frame. */
    '  if(uFlood<=0.0004&&uDrain<=0.0004){',
    '    vec4 s0=texture2D(tScene,uv);',
    '    if(s0.a>0.004){s0.rgb/=s0.a;}',
    '    gl_FragColor=vec4(s0.rgb,clamp(s0.a,0.0,1.0));',
    '    return;',
    '  }',
    '  float colIdS=floor(uv.x*70.0);',
    '  float rndS=hash11(colIdS);',
    /* The badge smears downward as the chrome takes over. */
    '  float smear=uFlood*uFlood;',
    '  vec2 suv=uv;',
    '  suv.y+=smear*0.62*(0.45+rndS*0.75);',
    '  suv.x+=(fbm(vec2(uv.y*7.0,uTime*0.4))-0.5)*0.035*smear;',
    '  vec4 s=texture2D(tScene,clamp(suv,vec2(0.001),vec2(0.999)));',
    '  if(s.a>0.004){s.rgb/=s.a;}',
    '  s.a*=1.0-smoothstep(0.55,1.0,uFlood);',
    GLSL_SHEET,
    '  vec3 rgb=mix(s.rgb,chrome,sheet);',
    '  float a=max(s.a,sheet);',
    '  gl_FragColor=vec4(rgb,clamp(a,0.0,1.0));',
    '}'
  ].join('\n');

  api.MELT_VERT = [
    'varying vec2 vUv;',
    'void main(){vUv=uv;gl_Position=vec4(position.xy,0.0,1.0);}'
  ].join('\n');

  /* cv-logo-3d.js reads these so the badge and the melt agree on quality, and
     so the entrance sizes itself off the same fragment budget rather than a
     second, separately-guessed device tier. */
  api.LOW_POWER = LOW_POWER;
  api.FBM_OCTAVES = FBM_OCT;
  api.COARSE_POINTER = COARSE;
  api.fitDpr = function (budgetPx, hardCap) {
    var d = Math.sqrt(budgetPx / viewportArea());
    return Math.max(BUDGET_FLOOR, Math.min(hardCap || hardDpr(), window.devicePixelRatio || 1, d));
  };

  /* Curtain: identical chrome, no scene behind it. */
  var CURTAIN_FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    UNIFORMS,
    GLSL_CHROME,
    'void main(){',
    '  vec2 uv=vUv;',
    GLSL_EDGES,
    '  if(sheet<=0.0008){gl_FragColor=vec4(0.0);return;}',
    GLSL_CHROME_EVAL,
    '  gl_FragColor=vec4(chrome,clamp(sheet,0.0,1.0));',
    '}'
  ].join('\n');

  /* Full-screen flowing chrome, used only while holding.
     The curtain shader carries sheet maths, lip highlights and an advancing
     edge — none of which is visible once the screen is fully covered. All you
     can see then is the metal moving, so the hold runs the same chrome function
     at a lower octave count and skips everything else. Roughly a third of the
     cost, which is what makes it affordable to keep running for seconds on a
     phone while a page loads. */
  var HOLD_OCT = LOW_POWER ? 2 : 3;
  var HOLD_FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform float uTime;',
    'uniform float uAspect;',
    chromeGLSL(HOLD_OCT),
    'void main(){',
    '  vec2 cp=vec2(vUv.x*uAspect,vUv.y)*3.0;',
    '  gl_FragColor=vec4(chromeColour(cp,uTime),1.0);',
    '}'
  ].join('\n');

  var CURTAIN_VERT = [
    'attribute vec2 aPos;',
    'varying vec2 vUv;',
    'void main(){vUv=aPos*0.5+0.5;gl_Position=vec4(aPos,0.0,1.0);}'
  ].join('\n');

  /* ---------- raw WebGL curtain ---------- */

  var glState = null;
  var glFailed = false;

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function initGL() {
    if (glState) return glState;
    if (glFailed) return null;

    try {
      var canvas = document.createElement('canvas');
      canvas.className = 'cv-chrome-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(canvas);

      var gl = canvas.getContext('webgl', {
        alpha: true, premultipliedAlpha: false, antialias: false,
        depth: false, stencil: false, powerPreference: 'low-power'
      }) || canvas.getContext('experimental-webgl');
      if (!gl) throw new Error('no webgl');

      var vs = compile(gl, gl.VERTEX_SHADER, CURTAIN_VERT);
      var fs = compile(gl, gl.FRAGMENT_SHADER, CURTAIN_FRAG);
      if (!vs || !fs) throw new Error('shader compile failed');

      var prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.bindAttribLocation(prog, 0, 'aPos');
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link failed');

      /* Both programs bind aPos to attribute 0, so they share the one triangle
         and the vertex state below without rebinding on every switch. */
      var hfs = compile(gl, gl.FRAGMENT_SHADER, HOLD_FRAG);
      var holdProg = null;
      if (hfs) {
        holdProg = gl.createProgram();
        gl.attachShader(holdProg, vs);
        gl.attachShader(holdProg, hfs);
        gl.bindAttribLocation(holdProg, 0, 'aPos');
        gl.linkProgram(holdProg);
        if (!gl.getProgramParameter(holdProg, gl.LINK_STATUS)) holdProg = null;
      }

      gl.useProgram(prog);

      /* One triangle covering the viewport. */
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      gl.disable(gl.DEPTH_TEST);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0);

      glState = {
        canvas: canvas,
        gl: gl,
        prog: prog,
        uTime: gl.getUniformLocation(prog, 'uTime'),
        uFlood: gl.getUniformLocation(prog, 'uFlood'),
        uDrain: gl.getUniformLocation(prog, 'uDrain'),
        uAspect: gl.getUniformLocation(prog, 'uAspect'),
        holdProg: holdProg,
        hTime: holdProg ? gl.getUniformLocation(holdProg, 'uTime') : null,
        hAspect: holdProg ? gl.getUniformLocation(holdProg, 'uAspect') : null,
        active: prog
      };
      resizeGL();
      sheen();
      window.addEventListener('resize', resizeGL, { passive: true });
      /* iPadOS and iOS fire orientationchange before the new innerWidth is
         readable, so the re-fit is deferred a frame. */
      window.addEventListener('orientationchange', function () {
        requestAnimationFrame(function () { requestAnimationFrame(resizeGL); });
      }, { passive: true });
      return glState;
    } catch (e) {
      glFailed = true;
      return null;
    }
  }

  var curDpr = 0;
  var lastArea = 0;

  function resizeGL() {
    if (!glState) return;
    /* Rotating a tablet or resizing a laptop window changes the fragment count,
       so the ceiling is re-derived rather than carried over from portrait. */
    var area = viewportArea();
    if (!curDpr) curDpr = startDpr();
    else if (lastArea && Math.abs(area - lastArea) / lastArea > 0.2) {
      curDpr = Math.min(curDpr, budgetDpr());
      setDurations();
    }
    lastArea = area;
    var dpr = curDpr;
    var w = Math.max(1, Math.round(window.innerWidth * dpr));
    var h = Math.max(1, Math.round(window.innerHeight * dpr));
    if (glState.canvas.width !== w || glState.canvas.height !== h) {
      glState.canvas.width = w;
      glState.canvas.height = h;
    }
    var g = glState.gl;
    g.viewport(0, 0, w, h);
    var aspect = window.innerWidth / window.innerHeight;
    g.useProgram(glState.prog);
    g.uniform1f(glState.uAspect, aspect);
    if (glState.holdProg) {
      g.useProgram(glState.holdProg);
      g.uniform1f(glState.hAspect, aspect);
    }
    g.useProgram(glState.active);
  }

  /* Called mid-animation when frames are coming in slow. Dropping the backing
     store is instant and, on a noise field, close to invisible — far better
     than letting the pour judder its way down the screen. */
  function degrade() {
    if (!glState || curDpr <= MIN_DPR) return false;
    curDpr = Math.max(MIN_DPR, curDpr * 0.7);
    resizeGL();
    /* Remembered for the rest of the session so the next transition starts at
       the level this device was measured to handle, rather than rediscovering
       it with another visibly rough animation. */
    rememberDpr(curDpr);
    setDurations();
    return true;
  }

  /* The counterpart: a device that sailed through should not stay penalised for
     a session because of one bad moment (a background tab waking, a thermal
     blip). Recovery is deliberately slower than degradation. */
  function recover() {
    if (!glState) return;
    var ceiling = budgetDpr();
    if (curDpr >= ceiling) return;
    curDpr = Math.min(ceiling, curDpr * 1.15);
    rememberDpr(curDpr);
    resizeGL();
  }

  function draw(t, flood, drain) {
    var s = glState;
    if (!s) return;
    if (s.active !== s.prog) { s.gl.useProgram(s.prog); s.active = s.prog; }
    s.gl.uniform1f(s.uTime, t);
    s.gl.uniform1f(s.uFlood, flood);
    s.gl.uniform1f(s.uDrain, drain);
    s.gl.clear(s.gl.COLOR_BUFFER_BIT);
    s.gl.drawArrays(s.gl.TRIANGLES, 0, 3);
  }

  /* Run flood or drain over a span, resolving when it finishes.
     Progress is wall-clock, so a dropped frame never slows the motion down —
     it just means the next frame lands further along. */
  function run(ms, onFrame) {
    return new Promise(function (resolve) {
      var t0 = performance.now();
      var last = t0;
      var slow = 0;
      var fast = 0;
      var dropped = 0;
      (function step() {
        var now = performance.now();
        var dt = now - last;
        last = now;
        /* Two consecutive frames over ~33ms means this device cannot hold 30fps
           at the current resolution. Give up pixels, not smoothness. */
        if (dt > 33 && dropped < 2) {
          slow++;
          if (slow >= 2) { slow = 0; if (degrade()) dropped++; }
        } else if (dt <= 33) {
          slow = 0;
          if (dt < 20) fast++;
        }
        var p = Math.min(1, (now - t0) / ms);
        onFrame(p, (now - t0) / 1000);
        if (p < 1) requestAnimationFrame(step);
        else { if (!dropped && fast > 12) recover(); resolve(); }
      })();
    });
  }

  /* ---------- CSS fallback ---------- */

  /* ---------- flowing hold ----------
     Capped rather than free-running: during an inbound hold the browser is
     parsing and laying out a new document on the same thread, and an uncapped
     loop would be taking frames away from the thing the visitor is waiting for.
     The chrome drifts slowly, so 30fps is indistinguishable from 60 here. */
  function holdFps() { return (COARSE || curDpr < 0.95) ? 24 : 30; }
  var holdRaf = 0;
  var holdT0 = 0;
  var holdLast = 0;
  var holdPhase = 0;

  function holdFlowing() {
    var s = glState;
    if (!s || !s.holdProg) return false;
    if (holdRaf) return true;

    holdT0 = performance.now();
    holdLast = 0;
    var interval = 1000 / holdFps();
    var slow = 0;
    var dropped = 0;

    (function step() {
      holdRaf = requestAnimationFrame(step);
      var now = performance.now();
      if (holdLast && now - holdLast < interval) return;

      /* This is the longest-running state in the whole transition — on bad wifi
         it can be several seconds — so it gets the same adaptive treatment as
         the animated passes rather than being left to grind. Measured against
         the interval it is actually aiming for, not a fixed 33ms, since the
         target here is 24 or 30fps by design. */
      if (holdLast && dropped < 2) {
        var over = (now - holdLast) > interval * 1.6;
        if (over) { slow++; if (slow >= 3) { slow = 0; if (degrade()) dropped++; } }
        else slow = 0;
      }

      holdLast = now;
      var g = s.gl;
      if (s.active !== s.holdProg) { g.useProgram(s.holdProg); s.active = s.holdProg; }
      /* Continues from where the flood left off instead of restarting the
         noise field, so entering the hold is not a visible cut. */
      g.uniform1f(s.hTime, holdPhase + (now - holdT0) / 1000);
      g.clear(g.COLOR_BUFFER_BIT);
      g.drawArrays(g.TRIANGLES, 0, 3);
    })();
    return true;
  }

  function holdStop() {
    if (holdRaf) { cancelAnimationFrame(holdRaf); holdRaf = 0; }
    if (holdT0) holdPhase += (performance.now() - holdT0) / 1000;
    holdT0 = 0;
  }

  /* Created once, alongside the canvas, so the hold never has to build DOM at
     the moment the browser is mid-navigation. */
  function sheen() {
    var el = document.querySelector('.cv-chrome-sheen');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cv-chrome-sheen';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<i></i><i></i>';
      (document.body || document.documentElement).appendChild(el);
    }
    return el;
  }

  function cssCurtain() {
    var el = document.querySelector('.cv-chrome-curtain');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cv-chrome-curtain';
      el.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(el);
    }
    return el;
  }

  /* ---------- soft navigation ----------
     The transition used to be two animations in two documents: the flood ran in
     the page you were leaving, then the browser threw that document away and a
     brand new one ran the drain. Nothing can join those — different canvases,
     different GL contexts, different script instances — which is why it read as
     two separate events however the timings were tuned.

     So the page is fetched and swapped in place instead. One canvas, one
     animation loop, one noise field, running continuously from the first frame
     of the pour to the last frame of the drain. The fetch also starts on click
     rather than after the flood, so the download overlaps the animation instead
     of following it.

     Any failure falls back to a hard navigation, which still has the inbound
     cover path below. */

  var SWAP_IDS = ['shopify-section-header', 'MainContent', 'shopify-section-footer'];
  var softCount = 0;

  function hardNavigate(url) {
    try { sessionStorage.setItem(INBOUND_KEY, '1'); } catch (e) {}
    window.location.href = url;
  }

  function fetchPage(url) {
    return fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'cv-chrome' } })
      .then(function (res) {
        if (!res.ok) throw new Error('bad status');
        var type = res.headers.get('content-type') || '';
        if (type.indexOf('text/html') === -1) throw new Error('not html');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        /* If the shape is not what we expect, this is not a page we can swap. */
        if (!doc.getElementById('MainContent')) throw new Error('no main');
        return doc;
      });
  }

  /* innerHTML-inserted scripts never execute. Sections in this theme carry
     inline scripts, so each one is recreated to run. */
  function runScripts(root) {
    var list = [].slice.call(root.querySelectorAll('script'));
    for (var i = 0; i < list.length; i++) {
      var old = list[i];
      var next = document.createElement('script');
      for (var j = 0; j < old.attributes.length; j++) {
        next.setAttribute(old.attributes[j].name, old.attributes[j].value);
      }
      next.text = old.textContent;
      old.parentNode.replaceChild(next, old);
    }
  }

  function swapIn(doc) {
    if (doc.title) document.title = doc.title;
    var pairs = [['link[rel="canonical"]', 'href'], ['meta[name="description"]', 'content']];
    for (var i = 0; i < pairs.length; i++) {
      var from = doc.querySelector(pairs[i][0]);
      var to = document.querySelector(pairs[i][0]);
      if (from && to) to.setAttribute(pairs[i][1], from.getAttribute(pairs[i][1]) || '');
    }

    var swapped = [];
    for (var k = 0; k < SWAP_IDS.length; k++) {
      var next = doc.getElementById(SWAP_IDS[k]);
      var cur = document.getElementById(SWAP_IDS[k]);
      if (!next || !cur) continue;
      cur.parentNode.replaceChild(document.adoptNode(next), cur);
      swapped.push(next);
    }
    for (var m = 0; m < swapped.length; m++) runScripts(swapped[m]);

    /* Dispatched on the section itself so it bubbles with the right target.
       This is the hook the carousel and the backdrop already use to re-init in
       the theme editor, so soft navigation reuses the theme's own convention
       rather than inventing a parallel one. */
    for (var n = 0; n < swapped.length; n++) {
      var sections = swapped[n].classList && swapped[n].classList.contains('shopify-section')
        ? [swapped[n]]
        : [].slice.call(swapped[n].querySelectorAll('.shopify-section'));
      for (var q = 0; q < sections.length; q++) {
        try {
          sections[q].dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        } catch (e) {}
      }
    }

    /* Best effort: Shopify's own analytics never sees a soft navigation. */
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && window.ShopifyAnalytics.lib.page) {
        window.ShopifyAnalytics.lib.page(null, { path: location.pathname, title: document.title });
      }
    } catch (e) {}

    /* Belt and braces: the reveal also listens for the section events above,
       but calling it directly means the scan has definitely run before
       contentReady starts waiting. */
    try { if (window.CVReveal && window.CVReveal.scan) window.CVReveal.scan(); } catch (e) {}

    return document.getElementById('MainContent') || document.body;
  }

  /* "Lets the screen load": the chrome does not start draining until the
     content behind it has actually painted, so it never reveals a page that is
     still assembling itself. */
  function contentReady(root, cap) {
    return new Promise(function (resolve) {
      var done = false;
      var timer = window.setTimeout(finish, cap);
      function finish() { if (done) return; done = true; window.clearTimeout(timer); resolve(); }

      var imgs = [].slice.call(root.querySelectorAll('img')).filter(function (im) {
        return im.getAttribute('loading') !== 'lazy' && !im.complete;
      });
      var fonts = (document.fonts && document.fonts.ready) ? document.fonts.ready : null;
      var pending = imgs.length + (fonts ? 1 : 0);
      if (!pending) { finish(); return; }

      function tick() { pending--; if (pending <= 0) finish(); }
      imgs.forEach(function (im) {
        im.addEventListener('load', tick, { once: true });
        im.addEventListener('error', tick, { once: true });
      });
      if (fonts) fonts.then(tick, tick);
    });
  }

  function softNavigate(url, push, restoreY) {
    var s = initGL();
    if (!s || !window.fetch || !window.DOMParser || !window.Promise) return false;

    s.canvas.classList.add('is-on');

    /* Started before the flood, not after it, so the network time is spent
       underneath the animation rather than queued behind it. */
    var pending = fetchPage(url);
    var gaveUp = false;
    var guard = new Promise(function (_, reject) {
      window.setTimeout(function () { gaveUp = true; reject(new Error('timeout')); }, holdCap() + FLOOD_MS);
    });

    run(FLOOD_MS, function (p, t) { draw(t, p, 0); }).then(function () {
      holdPhase = FLOOD_MS / 1000;
      document.documentElement.classList.add('cv-chrome-holding');
      if (!holdFlowing()) sheen();
      return Promise.race([pending, guard]);
    }).then(function (doc) {
      if (push) {
        try {
          history.replaceState({ cv: 1, y: window.scrollY || 0 }, '', location.href);
          history.pushState({ cv: 1, y: 0 }, '', url);
        } catch (e) {}
      }
      var main = swapIn(doc);
      window.scrollTo(0, restoreY || 0);
      softCount++;
      return contentReady(main, holdCap());
    }).then(function () {
      holdStop();
      document.documentElement.classList.remove('cv-chrome-holding');
      var phase = holdPhase;
      return run(DRAIN_MS, function (p, t) { draw(phase + t, 1, p); });
    }).then(function () {
      s.canvas.classList.remove('is-on');
      holdPhase = 0;
      navigating = false;
    })['catch'](function () {
      /* Whatever went wrong — offline, a redirect to a page we cannot swap, an
         app that rewrote the response — the visitor still gets where they were
         going. The chrome is already covering, so the reload is hidden. */
      if (!gaveUp) holdStop();
      hardNavigate(url);
    });

    return true;
  }

  window.addEventListener('popstate', function (e) {
    if (softCount === 0) return;          /* we never touched this entry */
    if (navigating) return;
    navigating = true;
    var y = (e.state && typeof e.state.y === 'number') ? e.state.y : 0;
    if (!softNavigate(window.location.href, false, y)) window.location.reload();
  });

  try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}

  /* ---------- leaving a page ---------- */

  /* A re-entry is a link that cannot be satisfied by swapping DOM — currently
     just the lobby, whose overlay lives outside #MainContent and whose scripts
     only ship on the index template. It deliberately does NOT get the chrome:
     the chrome reads as moving between shop pages, and arriving at the lobby is
     a different kind of move. It fades to the entrance's own background colour
     instead, so the next document opens on the shade it is already painted. */
  var FADE_KEY = 'cv-fade-inbound';
  var FADE_OUT_MS = 500;

  function fadeCurtain() {
    var el = document.querySelector('.cv-fade-curtain');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cv-fade-curtain';
      el.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(el);
    }
    return el;
  }

  api.reenter = function (url) {
    if (navigating) return;
    navigating = true;

    if (reduced) { window.location.href = url; return; }

    var went = false;
    function go() {
      if (went) return;
      went = true;
      try { sessionStorage.setItem(FADE_KEY, '1'); } catch (e) {}
      window.location.href = url;
    }

    var el = fadeCurtain();
    /* Forces the starting opacity to be committed, so the class change is a
       transition rather than an instant jump to black. */
    void el.offsetWidth;
    el.classList.add('is-on');
    window.setTimeout(go, FADE_OUT_MS);
  };

  /* Arriving at the lobby behind the flat cover. */
  function onArriveFade() {
    var root = document.documentElement;
    if (!root.classList.contains('cv-fade-inbound')) return false;
    try { sessionStorage.removeItem(FADE_KEY); } catch (e) {}

    if (reduced) { root.classList.remove('cv-fade-inbound'); return true; }

    var done = false;
    function reveal() {
      if (done) return;
      done = true;
      root.classList.add('cv-fade-out');
      window.setTimeout(function () {
        root.classList.remove('cv-fade-inbound', 'cv-fade-out');
        navigating = false;
      }, 900);
    }

    /* Held until the badge has actually painted, not merely until the document
       loaded — the entrance boots from a dynamically imported module that
       window.load never waits for, so fading up on load alone would reveal an
       empty stage. */
    var cap = window.setTimeout(reveal, holdCap() + 1200);
    function ready() { window.clearTimeout(cap); reveal(); }

    var loaded = (document.readyState === 'complete')
      ? Promise.resolve()
      : new Promise(function (res) { window.addEventListener('load', res, { once: true }); });
    var entrance = window.CVEntranceReady;

    if (entrance && typeof entrance.then === 'function') {
      Promise.all([loaded, entrance]).then(ready, ready);
    } else {
      loaded.then(ready, ready);
    }
    return true;
  }

  api.navigate = function (url) {
    if (navigating) return;
    navigating = true;

    if (reduced) { window.location.href = url; return; }

    /* One continuous transition in this document. */
    if (softNavigate(url, true, 0)) return;

    /* No fetch, no DOMParser, or no WebGL: fall back to the old two-document
       behaviour, where the inbound page picks the cover back up. */
    try { sessionStorage.setItem(INBOUND_KEY, '1'); } catch (e) {}

    var went = false;
    function go() { if (!went) { went = true; window.location.href = url; } }

    var el = cssCurtain();
    el.classList.add('is-armed');
    void el.offsetWidth;
    el.classList.add('is-covering');
    window.setTimeout(go, 300);
  };

  /* ---------- arriving on a page ---------- */

  function uncover() {
    holdStop();
    holdPhase = 0;
    document.documentElement.classList.remove('cv-chrome-inbound', 'cv-chrome-holding', 'cv-fade-inbound', 'cv-fade-out');
    var fade = document.querySelector('.cv-fade-curtain');
    if (fade) fade.classList.remove('is-on');
    if (glState) glState.canvas.classList.remove('is-on');
    var el = document.querySelector('.cv-chrome-curtain');
    if (el) el.classList.remove('is-armed', 'is-covering', 'is-leaving');
  }

  /* ---------- holding while the page catches up ----------
     The chrome sits fully covering until the destination has actually finished
     loading, then drains onto a finished page instead of onto a half-built one.
     On a fast connection this resolves in a couple of hundred milliseconds and
     is invisible; on bad wifi it is the pause the visitor sees.

     Nothing is redrawn while holding. uFlood is 1, so every pixel is covered
     and the shader's early-out never fires — animating here would be the single
     most expensive thing this file can do, at exactly the moment the browser is
     busy parsing a new document. The last drawn frame stays on screen for free
     and a composited CSS sheen slides across it so it reads as molten rather
     than frozen. */
  function holdUntilReady(onReady) {
    var released = false;
    var started = performance.now();
    var cap = holdCap();
    var capTimer = 0;

    function release() {
      if (released) return;
      released = true;
      window.clearTimeout(capTimer);
      window.removeEventListener('load', onLoad);
      var waited = performance.now() - started;
      var remaining = Math.max(0, HOLD_MIN_MS - waited);
      window.setTimeout(function () {
        holdStop();
        document.documentElement.classList.remove('cv-chrome-holding');
        onReady();
      }, remaining);
    }
    function onLoad() { release(); }

    document.documentElement.classList.add('cv-chrome-holding');
    /* sheen is the no-WebGL consolation prize only — with GL the chrome itself
       is what moves. */
    if (!holdFlowing()) sheen();
    capTimer = window.setTimeout(release, cap);

    /* On the lobby, "loaded" is not the same as "ready": the badge boots from a
       dynamically imported module that window.load never waits for. Where the
       entrance publishes a signal, both have to land before the chrome moves. */
    var entrance = window.CVEntranceReady;
    if (entrance && typeof entrance.then === 'function') {
      var loaded = (document.readyState === 'complete')
        ? Promise.resolve()
        : new Promise(function (res) { window.addEventListener('load', res, { once: true }); });
      Promise.all([loaded, entrance]).then(release, release);
      return;
    }

    if (document.readyState === 'complete') release();
    else window.addEventListener('load', onLoad);
  }

  function onArrive() {
    if (onArriveFade()) return;
    if (!document.documentElement.classList.contains('cv-chrome-inbound')) return;
    try { sessionStorage.removeItem(INBOUND_KEY); } catch (e) {}
    if (reduced) { uncover(); return; }

    var s = initGL();
    if (s) {
      s.canvas.classList.add('is-on');
      /* Paint one fully-covered frame, then hand over from the CSS cover that
         the <head> snippet painted, so the swap never shows a seam. */
      draw(0, 1, 0);
      requestAnimationFrame(function () {
        document.documentElement.classList.remove('cv-chrome-inbound');
        holdUntilReady(function () {
          /* Pick the noise up where the hold left it so the drain starts from
             the exact frame that was on screen. */
          var phase = holdPhase;
          run(DRAIN_MS, function (p, t) { draw(phase + t, 1, p); }).then(function () {
            s.canvas.classList.remove('is-on');
            holdPhase = 0;
            navigating = false;
          });
        });
      });
      return;
    }

    var el = cssCurtain();
    el.classList.add('is-armed', 'is-covering');
    requestAnimationFrame(function () {
      document.documentElement.classList.remove('cv-chrome-inbound');
      holdUntilReady(function () {
        el.classList.add('is-leaving');
        window.setTimeout(uncover, 460);
      });
    });
  }

  /* ---------- link interception ---------- */

  function shouldIntercept(a) {
    if (!a || !a.getAttribute) return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    if (a.hasAttribute('data-cv-no-transition')) return false;

    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return false;
    if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return false;
    if (a.protocol !== 'http:' && a.protocol !== 'https:') return false;
    if (a.host !== window.location.host) return false;

    /* Anything that takes money or touches an account loads normally. */
    if (/^\/(cart|checkout|account|apps|tools)(\/|$|\?)/.test(a.pathname)) return false;
    if (a.pathname === window.location.pathname && a.search === window.location.search) return false;
    return true;
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.closest('[data-cv-entrance]')) return;   /* entrance runs its own melt */

    /* Checked before shouldIntercept, which bails on a link pointing at the URL
       you are already on. That guard is right for ordinary links and wrong for
       this one: pressing Lobby while sitting at /?intro should still re-enter,
       and previously fell through to a bare reload with no transition at all. */
    if (a.hasAttribute('data-cv-reentry') && a.host === window.location.host) {
      e.preventDefault();
      api.reenter(a.href);
      return;
    }

    if (!shouldIntercept(a)) return;

    e.preventDefault();
    api.navigate(a.href);
  }, false);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onArrive);
  } else {
    onArrive();
  }

  /* Back/forward cache must never restore a covered page. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    try { sessionStorage.removeItem(INBOUND_KEY); sessionStorage.removeItem(FADE_KEY); } catch (err) {}
    navigating = false;
    uncover();
  });

  return api;
})();
